import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Sparkles, Rocket, Zap, Wrench, Check } from 'lucide-react';
import axios from '../../axiosConfig';
import Badge from '../../components/ui/Badge';
import './ChangelogDropdown.css';

// "What's New" header dropdown. Two render modes:
//   1) default — renders its own trigger button + panel (desktop)
//   2) controlled — parent owns open state; we render only the panel
//      (used by HeaderOverflowMenu on mobile so the same panel powers
//      both the desktop trigger and the mobile overflow menu item).

const CATEGORY_META = {
  FEATURE:     { label: 'New',         icon: Rocket, color: '#0d7463' },
  IMPROVEMENT: { label: 'Improvement', icon: Zap,    color: '#2563eb' },
  FIX:         { label: 'Fix',         icon: Wrench, color: '#7c3aed' },
};

const formatDate = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

/**
 * The panel itself (presentational). Mounted both inline by ChangelogDropdown
 * and reused by HeaderOverflowMenu on mobile. The mount point's containing
 * `.header-actions` provides `position: relative` so the panel anchors right.
 */
export const ChangelogPanel = ({ onClose, panelRef }) => {
  const [entries, setEntries] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/merchant/changelog');
      setEntries(res.data?.entries || []);
      setUnreadCount(res.data?.unreadCount || 0);
    } catch {
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const markRead = async (id) => {
    if (entries.find((e) => e.id === id)?.isRead) return;
    setEntries((prev) => prev.map((e) => (e.id === id ? { ...e, isRead: true } : e)));
    setUnreadCount((prev) => Math.max(prev - 1, 0));
    try { await axios.post(`/merchant/changelog/${id}/read`); } catch { /* optimistic */ }
  };

  const markAllRead = async () => {
    if (unreadCount === 0) return;
    setEntries((prev) => prev.map((e) => ({ ...e, isRead: true })));
    setUnreadCount(0);
    try { await axios.post('/merchant/changelog/read-all'); } catch { /* optimistic */ }
  };

  const toggleExpand = (entry) => {
    setExpandedId((prev) => (prev === entry.id ? null : entry.id));
    markRead(entry.id);
  };

  return (
    <div ref={panelRef} className="changelog-panel" role="dialog" aria-label="What's new">
      <div className="changelog-header">
        <div className="changelog-title">
          <Sparkles size={16} />
          <span>What's New</span>
        </div>
        {unreadCount > 0 && (
          <button type="button" className="changelog-mark-all" onClick={markAllRead}>
            <Check size={13} /> Mark all read
          </button>
        )}
      </div>

      <div className="changelog-body">
        {loading ? (
          <div className="changelog-state">Loading…</div>
        ) : entries.length === 0 ? (
          <div className="changelog-state">
            <Sparkles size={28} className="changelog-state-icon" />
            <p>No updates yet. Check back soon.</p>
          </div>
        ) : (
          <ul className="changelog-list">
            {entries.map((e) => {
              const meta = CATEGORY_META[e.category] || CATEGORY_META.FEATURE;
              const Icon = meta.icon;
              const isExpanded = expandedId === e.id;
              return (
                <li
                  key={e.id}
                  className={`changelog-item ${e.isRead ? '' : 'is-unread'} ${isExpanded ? 'is-expanded' : ''}`}
                >
                  <button type="button" className="changelog-item-btn" onClick={() => toggleExpand(e)}>
                    <span className="changelog-cat" style={{ background: meta.color }}>
                      <Icon size={11} />
                      {meta.label}
                    </span>
                    <span className="changelog-item-content">
                      <span className="changelog-item-title">
                        {e.title}
                        {!e.isRead && <span className="changelog-dot" aria-label="Unread" />}
                      </span>
                      <span className="changelog-item-summary">{e.summary}</span>
                      <span className="changelog-item-date">{formatDate(e.publishedAt)}</span>
                    </span>
                  </button>
                  {isExpanded && e.body && (
                    <div className="changelog-item-body">{e.body}</div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

/**
 * Default trigger + panel (desktop). Hidden on mobile via `.ha-desktop-only`
 * (see HeaderActions.css). Mobile uses HeaderOverflowMenu instead.
 */
const ChangelogDropdown = () => {
  const [open, setOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  // Lightweight count poll on mount so the badge is correct before the panel
  // is ever opened.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get('/merchant/changelog/unread-count');
        if (!cancelled) setUnreadCount(res.data?.unreadCount || 0);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (ev) => {
      const t = triggerRef.current;
      const p = panelRef.current;
      if (t && t.contains(ev.target)) return;
      if (p && p.contains(ev.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="header-action-btn changelog-trigger ha-desktop-only"
        onClick={() => setOpen((v) => !v)}
        aria-label={unreadCount > 0 ? `What's new (${unreadCount} unread)` : "What's new"}
        aria-expanded={open}
      >
        <Sparkles size={18} aria-hidden="true" />
        {unreadCount > 0 && (
          <Badge variant="error" size="sm" className="changelog-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </Badge>
        )}
      </button>

      {open && <ChangelogPanel panelRef={panelRef} onClose={() => setOpen(false)} />}
    </>
  );
};

export default ChangelogDropdown;
